import { CalendarIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { type DateRange } from 'react-day-picker';

import { Button } from '@apps/web/components/ui/button';
import { Calendar } from '@apps/web/components/ui/calendar';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@apps/web/components/ui/popover';
import {
    dateKeyFromLocalCalendarDate,
    formatDateKey,
    getDateStringInTimezone,
    localCalendarDateFromKey,
    shiftDateKey,
    type WeekdayIndex,
} from '@apps/web/lib/date_utils';
import { cn } from '@apps/web/lib/utils';

export type DateRangePickerValue = {
    readonly from: string;
    readonly to: string;
};

type DateRangePickerProps = {
    readonly value?: DateRangePickerValue;
    readonly timezone: string;
    readonly onChange: (value: DateRangePickerValue) => void;
    readonly onClear?: () => void;
    readonly className?: string;
    readonly direction?: 'backward' | 'forward';
    readonly weekStartsOn?: WeekdayIndex;
};

type Preset = {
    readonly label: string;
    readonly value: DateRangePickerValue;
};

function padNumber(value: number): string {
    return String(value).padStart(2, '0');
}

function startOfMonthDateKey(dateKey: string): string {
    return `${dateKey.slice(0, 7)}-01`;
}

function endOfMonthDateKey(dateKey: string): string {
    const [year, month] = dateKey.split('-').map(Number);
    const normalizedYear = year ?? 2000;
    const normalizedMonth = month ?? 1;
    const lastDay = new Date(
        Date.UTC(normalizedYear, normalizedMonth, 0),
    ).getUTCDate();

    return [
        normalizedYear,
        padNumber(normalizedMonth),
        padNumber(lastDay),
    ].join('-');
}

function shiftMonthDateKey(dateKey: string, months: number): string {
    const [year, month, day] = dateKey.split('-').map(Number);
    const normalizedYear = year ?? 2000;
    const normalizedMonth = month ?? 1;
    const normalizedDay = day ?? 1;
    const shiftedMonthStart = new Date(
        Date.UTC(normalizedYear, normalizedMonth - 1, 1),
    );
    shiftedMonthStart.setUTCMonth(shiftedMonthStart.getUTCMonth() + months);

    const shiftedYear = shiftedMonthStart.getUTCFullYear();
    const shiftedMonth = shiftedMonthStart.getUTCMonth() + 1;
    const shiftedMonthLastDay = new Date(
        Date.UTC(shiftedYear, shiftedMonth, 0),
    ).getUTCDate();

    return [
        shiftedYear,
        padNumber(shiftedMonth),
        padNumber(Math.min(normalizedDay, shiftedMonthLastDay)),
    ].join('-');
}

function buildPresets(timezone: string): Array<Preset> {
    const todayDateKey = getDateStringInTimezone(new Date(), timezone);
    const lastMonthReference = shiftMonthDateKey(todayDateKey, -1);

    return [
        {
            label: 'Last 7 days',
            value: {
                from: shiftDateKey(todayDateKey, -6),
                to: todayDateKey,
            },
        },
        {
            label: 'Last 30 days',
            value: {
                from: shiftDateKey(todayDateKey, -29),
                to: todayDateKey,
            },
        },
        {
            label: 'This month',
            value: {
                from: startOfMonthDateKey(todayDateKey),
                to: todayDateKey,
            },
        },
        {
            label: 'Last month',
            value: {
                from: startOfMonthDateKey(lastMonthReference),
                to: endOfMonthDateKey(lastMonthReference),
            },
        },
    ];
}

function buildForwardPresets(timezone: string): Array<Preset> {
    const todayDateKey = getDateStringInTimezone(new Date(), timezone);
    const nextMonthReference = shiftMonthDateKey(todayDateKey, 1);

    return [
        {
            label: 'Next 7 days',
            value: { from: todayDateKey, to: shiftDateKey(todayDateKey, 6) },
        },
        {
            label: 'Next 30 days',
            value: { from: todayDateKey, to: shiftDateKey(todayDateKey, 29) },
        },
        {
            label: 'This month',
            value: {
                from: todayDateKey,
                to: endOfMonthDateKey(todayDateKey),
            },
        },
        {
            label: 'Next month',
            value: {
                from: startOfMonthDateKey(nextMonthReference),
                to: endOfMonthDateKey(nextMonthReference),
            },
        },
    ];
}

function buildCalendarValue(
    value: DateRangePickerValue | undefined,
): DateRange {
    if (!value) {
        return { from: undefined };
    }
    return {
        from: localCalendarDateFromKey(value.from),
        to: localCalendarDateFromKey(value.to),
    };
}

function buildPickerValue(range: DateRange): DateRangePickerValue | null {
    if (!range.from) {
        return null;
    }

    const from = dateKeyFromLocalCalendarDate(range.from);
    const to = dateKeyFromLocalCalendarDate(range.to ?? range.from);
    if (from <= to) {
        return { from, to };
    }
    return { from: to, to: from };
}

export function DateRangePicker({
    value,
    timezone,
    onChange,
    onClear,
    className,
    direction = 'backward',
    weekStartsOn,
}: DateRangePickerProps) {
    const [open, setOpen] = useState(false);
    const [draftRange, setDraftRange] = useState<DateRange>(() =>
        buildCalendarValue(value),
    );
    const presets = useMemo(
        () =>
            direction === 'forward'
                ? buildForwardPresets(timezone)
                : buildPresets(timezone),
        [direction, timezone],
    );
    const selectedRange = useMemo(() => buildCalendarValue(value), [value]);
    const maxDateKey = getDateStringInTimezone(new Date(), timezone);
    const draftValue = buildPickerValue(draftRange);

    const handleOpenChange = (nextOpen: boolean) => {
        setOpen(nextOpen);
        if (nextOpen) {
            setDraftRange(selectedRange);
        }
    };

    const applyDraftRange = () => {
        if (draftValue === null) {
            return;
        }
        onChange(draftValue);
        setOpen(false);
    };

    return (
        <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild={true}>
                <Button
                    variant="outline"
                    className={cn(
                        'w-full justify-start text-left font-normal',
                        className,
                    )}
                >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {!value
                        ? 'Select date range'
                        : value.from === value.to
                          ? formatDateKey(value.from, timezone)
                          : `${formatDateKey(value.from, timezone)} - ${formatDateKey(
                                value.to,
                                timezone,
                            )}`}
                </Button>
            </PopoverTrigger>
            <PopoverContent
                className="flex w-auto flex-col gap-3 p-0"
                align="end"
            >
                <div className="grid grid-cols-2 gap-2 border-b p-3">
                    {presets.map((preset) => {
                        const isActive =
                            value !== undefined &&
                            preset.value.from === value.from &&
                            preset.value.to === value.to;

                        return (
                            <Button
                                key={preset.label}
                                type="button"
                                variant={isActive ? 'default' : 'outline'}
                                className="justify-start"
                                onClick={() => {
                                    onChange(preset.value);
                                    setOpen(false);
                                }}
                            >
                                {preset.label}
                            </Button>
                        );
                    })}
                    {value !== undefined && onClear !== undefined ? (
                        <Button
                            type="button"
                            variant="ghost"
                            className="justify-start"
                            aria-label="Clear date range"
                            onClick={() => {
                                onClear();
                                setOpen(false);
                            }}
                        >
                            Clear
                        </Button>
                    ) : null}
                </div>
                <Calendar
                    mode="range"
                    selected={open ? draftRange : selectedRange}
                    numberOfMonths={2}
                    weekStartsOn={weekStartsOn}
                    disabled={
                        direction === 'forward'
                            ? undefined
                            : (date) =>
                                  dateKeyFromLocalCalendarDate(date) >
                                  maxDateKey
                    }
                    onSelect={(nextRange, selectedDay) => {
                        if (draftRange.from && draftRange.to) {
                            setDraftRange({ from: selectedDay });
                            return;
                        }
                        setDraftRange(nextRange ?? selectedRange);
                    }}
                />
                <div className="flex justify-end gap-2 border-t p-3">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                            setDraftRange(selectedRange);
                            setOpen(false);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        disabled={draftValue === null}
                        onClick={applyDraftRange}
                    >
                        Apply
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    );
}
